import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Sparkles, BookMarked, Target, Wind, Eye, Flame, CalendarDays } from "lucide-react";
import { Starfield } from "../components/Starfield";
import { Header } from "../components/Header";
import { Button } from "../components/ui/button";
import { api } from "../lib/api";

export default function Metrics() {
  const navigate = useNavigate();
  const [m, setM] = useState(null);

  useEffect(() => {
    api.get("/metrics").then(({ data }) => setM(data)).catch(() => {});
  }, []);

  const maxActivity = m ? Math.max(1, ...m.activity.map((a) => a.count)) : 1;

  const big = m
    ? [
        { icon: Sparkles, label: "Patterns cleared", value: m.patterns_cleared, tone: "text-cg-violetglow" },
        { icon: BookMarked, label: "Insights saved", value: m.insights_saved, tone: "text-cg-cyan" },
        { icon: Flame, label: "Current streak", value: `${m.current_streak}d`, tone: "text-cg-gold" },
      ]
    : [];

  const small = m
    ? [
        { icon: Eye, label: "Reflections", value: m.reflections },
        { icon: Target, label: "Intentions set", value: m.intentions_set },
        { icon: Wind, label: "Quiet minutes", value: m.quiet_minutes },
        { icon: CalendarDays, label: "Active days", value: m.active_days },
      ]
    : [];

  return (
    <div className="relative min-h-screen bg-cg-bg">
      <Starfield />
      <Header />
      <section className="relative z-10 max-w-5xl mx-auto px-5 pt-32 pb-24">
        <p className="font-mono text-xs uppercase tracking-[0.3em] text-cg-violetglow mb-3">Your journey</p>
        <h1 className="font-serif text-3xl sm:text-4xl font-extralight tracking-tight mb-2">Ascension metrics</h1>
        <p className="text-cg-texts mb-10">A gentle mirror of how far you've traveled inward.</p>

        {!m && <div className="h-10 w-10 rounded-full border-2 border-cg-violet border-t-transparent animate-spin" />}

        {m && (m.total_sessions === 0 ? (
          <div className="rounded-2xl p-10 cg-glass text-center">
            <p className="font-serif text-xl text-cg-textp mb-3">Your constellation is still forming</p>
            <p className="text-sm text-cg-texts mb-7 max-w-sm mx-auto">Begin a session and your patterns cleared, insights and streaks will bloom here.</p>
            <Button data-testid="metrics-begin-button" onClick={() => navigate("/app")} className="rounded-full bg-cg-violet hover:bg-cg-violetglow text-white px-7">
              Begin a session
            </Button>
          </div>
        ) : (
          <>
            <div className="grid sm:grid-cols-3 gap-5 mb-6">
              {big.map((c, i) => (
                <motion.div
                  key={c.label}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: i * 0.1 }}
                  className="rounded-2xl p-7 bg-cg-card border border-[rgba(140,79,214,0.22)] shadow-[0_8px_32px_rgba(7,8,20,0.5)]"
                  data-testid={`metric-${c.label.toLowerCase().split(" ")[0]}`}
                >
                  <c.icon className={`h-6 w-6 mb-4 ${c.tone}`} />
                  <p className="font-serif text-4xl text-cg-textp">{c.value}</p>
                  <p className="text-sm text-cg-textm mt-1">{c.label}</p>
                </motion.div>
              ))}
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-10">
              {small.map((c) => (
                <div key={c.label} className="rounded-2xl p-5 bg-cg-card border border-[rgba(140,79,214,0.18)]">
                  <c.icon className="h-4 w-4 text-cg-cyan mb-2" />
                  <p className="font-serif text-2xl text-cg-textp">{c.value}</p>
                  <p className="text-xs text-cg-textm mt-0.5">{c.label}</p>
                </div>
              ))}
            </div>

            <div className="rounded-2xl p-7 bg-cg-card border border-[rgba(140,79,214,0.22)]">
              <div className="flex items-center justify-between mb-6">
                <h2 className="font-serif text-lg text-cg-textp">Last 7 days</h2>
                <span className="text-xs text-cg-textm">Longest streak · {m.longest_streak}d</span>
              </div>
              <div className="flex items-end justify-between gap-3 h-40" data-testid="activity-chart">
                {m.activity.map((a) => (
                  <div key={a.date} className="flex-1 flex flex-col items-center gap-2">
                    <motion.div
                      initial={{ height: 0 }}
                      animate={{ height: `${(a.count / maxActivity) * 100}%` }}
                      transition={{ duration: 0.6 }}
                      className={`w-full rounded-t-lg min-h-[4px] ${a.count ? "bg-gradient-to-t from-cg-violet to-cg-cyan" : "bg-[rgba(140,79,214,0.15)]"}`}
                      title={`${a.count} messages`}
                    />
                    <span className="text-[10px] font-mono text-cg-textm">{a.day}</span>
                  </div>
                ))}
              </div>
            </div>
          </>
        ))}
      </section>
    </div>
  );
}
